/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  DatePicker,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

// utils/apis/constants
import { checkToken } from 'api/utils';
import currencyFormatter from 'utils/currencyFormatter';
import moment from 'moment';
import {
  getCalculosAnualesISR,
  getCalculoAnualISR,
  patchCalculoAnualISR,
  postCalculoAnualISR,
} from 'api/nominas/calculoISRAnual/calculosAnualesISR';
import { getEstadosGlobales } from 'api/catalogos';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import { onError, onSuccess } from 'utils/handlers';
import { formatReceivedValues } from 'utils/formatValues';
import {
  ESTATUS_ACTIVO,
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

// componentes
import Select from 'components/Select';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import CurrencyInput from 'components/CurrencyInput';
import NumericInput from 'components/NumericInput';
import ModalConfirmation from 'components/ModalConfirmation';

// Modulos
import IsrCalculoAnualDetalle from './isrCalculoAnualDetalle';

export const calculoAnualISRPermission = {
  permissionModel: 'isrcalculoanual',
  catalogsModels: [],
  tabsModels: [],
};

const IsrCalculoAnual = ({ permission }) => {
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState();
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [tiposDeNominasEmpleados, setTiposDeNominasEmpleados] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [estados, setEstados] = useState([]);
  const [confirmationVisibleAutorizar, setConfirmationVisibleAutorizar] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [confirmationVisibleCancelar, setConfirmationVisibleCancelar] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [estatusCancelado, setEstatusCancelado] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCalculosAnualesISR();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _ejercicio,
        _tipos_de_nomina_empleados,
        _estados,
        _data,
      ] = await Promise.all([
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getEstadosGlobales(),
        getCalculosAnualesISR(),
      ]);
      if (mounted) {
        setPeriodosFiscales(_ejercicio);
        setTiposDeNominasEmpleados(_tipos_de_nomina_empleados);
        setEstados(_estados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setSelectedRowData();
    setEstatusAutorizado(false);
    setEstatusCancelado(false);
    form.resetFields();
    setVisible(false);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    await fetchData();
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _calculoAnualIsr = await getCalculoAnualISR(match.id);
    if (_calculoAnualIsr?.estados_globales === ESTATUS_ACTIVO
      || _calculoAnualIsr?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO
      || estado?.id === ESTATUS_ACTIVO));
    }
    if (_calculoAnualIsr?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_calculoAnualIsr?.estados_globales === ESTATUS_CANCELADO) {
      setEstadosFiltered(estados?.filter((estado) => estado?.id === ESTATUS_CANCELADO));
      setEstatusCancelado(true);
    }
    setTimeout(() => {
      if (_calculoAnualIsr) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedValues(_calculoAnualIsr));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchCalculoAnualISR(key, values) : postCalculoAnualISR(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const onClickAdd = async () => {
    onCancel();
    await fetchData();
    setEstadosFiltered(estados);
    form.setFieldsValue({
      fecha_de_calculo: moment(new Date().getDate(), 'DD/MM/YYYY'),
    });
    setVisible(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const fecha_calculo = moment(selectedRowData?.fecha_de_calculo, 'YYYY-MM-DD');
      const vals = {
        ...selectedRowData,
        fecha_de_calculo: fecha_calculo,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchCalculoAnualISR(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisibleAutorizar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar una nómina ya cancelada');
        return;
      }
      const fecha_calculo = moment(selectedRowData?.fecha_de_calculo, 'YYYY-MM-DD');
      const vals = {
        ...selectedRowData,
        fecha_de_calculo: fecha_calculo,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchCalculoAnualISR(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisibleCancelar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const handleContentPrint = () => {
    const ejercicio = periodosFiscales?.find((p) => p?.id === selectedRowData?.periodo_fiscal);
    const tipo_nomina_emp = tiposDeNominasEmpleados?.find((tne) => tne?.id === selectedRowData?.tipo_de_nomina_empleado);
    return `Cálculo Anual de ISR ${ejercicio?.descripcion} ${tipo_nomina_emp?.descripcion}`;
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedRowData(record);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Fecha de Cálculo',
      dataIndex: 'fecha_de_calculo',
      key: 'fecha_de_calculo',
      width: 300,
    },
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 300,
      render: (val) => periodosFiscales?.find((periodo) => periodo?.id === val)?.descripcion,
    },
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 300,
      render: (val) => tiposDeNominasEmpleados?.find((tne) => tne?.id === val)?.descripcion,
    },
    {
      titleText: 'No. de Empleados con Cálculo Anual de ISR',
      dataIndex: 'numero_de_empleados_con_calculo',
      key: 'numero_de_empleados_con_calculo',
      width: 350,
    },
    {
      titleText: 'No. de Empleados sin Cálculo Anual de ISR',
      dataIndex: 'numero_de_empleados_sin_calculo',
      key: 'numero_de_empleados_sin_calculo',
      width: 350,
    },
    {
      titleText: 'ISR a Cargo',
      dataIndex: 'total_isr_cargo',
      key: 'total_isr_cargo',
      width: 300,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR a Favor',
      dataIndex: 'total_isr_favor',
      key: 'total_isr_favor',
      width: 300,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="cargando...." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
          name="general"
        >
          {!open ? (
            <>
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Ejercicio"
                    name="ejercicio"
                    hasFeedback
                  >
                    <Select
                      dataSource={periodosFiscales}
                      onChange={async (val) => {
                        setLoading(true);
                        if (val) {
                          if (form.getFieldValue('tipo_nomina_empleado')) {
                            const _data = await getCalculosAnualesISR({ periodo_fiscal: val });
                            const _dataFiltered = _data?.filter((d) => d?.tipo_de_nomina_empleado === form.getFieldValue('tipo_nomina_empleado'));
                            setData(_dataFiltered);
                            return setLoading(false);
                          }
                          const _data = await getCalculosAnualesISR({ periodo_fiscal: val });
                          setData(_data);
                          return setLoading(false);
                        }
                        if (form.getFieldValue('tipo_nomina_empleado')) {
                          const _data = await getCalculosAnualesISR({ tipo_de_nomina_empleado: form.getFieldValue('tipo_nomina_empleado') });
                          setData(_data);
                          return setLoading(false);
                        }
                        fetchData();
                        return setLoading(false);
                      }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Tipo de Nómina-Empleado"
                    name="tipo_nomina_empleado"
                    hasFeedback
                  >
                    <Select
                      dataSource={tiposDeNominasEmpleados}
                      onChange={async (val) => {
                        setLoading(true);
                        if (val) {
                          if (form.getFieldValue('ejercicio')) {
                            const _data = await getCalculosAnualesISR({ tipo_de_nomina_empleado: val });
                            const _dataFiltered = _data?.filter((d) => d?.periodo_fiscal === form.getFieldValue('ejercicio'));
                            setData(_dataFiltered);
                            return setLoading(false);
                          }
                          const _data = await getCalculosAnualesISR({ tipo_de_nomina_empleado: val });
                          setData(_data);
                          return setLoading(false);
                        }
                        if (form.getFieldValue('ejercicio')) {
                          const _data = await getCalculosAnualesISR({ periodo_fiscal: form.getFieldValue('ejercicio') });
                          setData(_data);
                          return setLoading(false);
                        }
                        fetchData();
                        return setLoading(false);
                      }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Table
                data={data}
                cols={columns}
                controls={{
                  onClickAdd,
                  onClickEdit,
                }}
                customActions={[
                  {
                    onClick: () => setConfirmationVisibleAutorizar(true),
                    permission: { model: 'isrcalculoanual', action: 'autorizar' },
                    text: 'Autorizar',
                    icon: <CheckCircleOutlined />,
                    disabledBySelection: false,
                    className: 'success',
                    disabled: !selectedRowKeys?.length
                      || selectedRowData?.estados_globales === ESTATUS_AUTORIZADO
                      || selectedRowData?.estados_globales === ESTATUS_CANCELADO
                      || selectedRowData?.estados_globales === ESTATUS_INACTIVO,
                  },
                  {
                    permission: { model: 'isrcalculoanual', action: 'cancelar' },
                    onClick: () => setConfirmationVisibleCancelar(true),
                    text: 'Cancelar',
                    icon: <CloseCircleOutlined />,
                    disabledBySelection: false,
                    className: 'cancel',
                    disabled: !selectedRowKeys?.length
                      || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO,
                  },
                ]}
                editText="Detalle"
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                allowExportParam={false}
                permission={permission}
              />
            </>
          ) : (
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'Detalle' : 'Agregar'} Cálculo Anual`}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onCancel={onCancel}
                  loading={loading}
                  mutable={!selectedRowKeys?.length}
                  onFinishText="Guardar y Continuar"
                />
              )}
              bordered={false}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Ejercicio"
                    name="periodo_fiscal"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={periodosFiscales}
                      disabled={selectedRowKeys?.length || estatusAutorizado || estatusCancelado}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Tipo de Nómina Empleado"
                    name="tipo_de_nomina_empleado"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={tiposDeNominasEmpleados}
                      disabled={selectedRowKeys?.length || estatusAutorizado || estatusCancelado}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <CurrencyInput
                    label="Total ISR a Cargo"
                    name="total_isr_cargo"
                    disabled
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="No. de Empleados con Cálculo Anual de ISR"
                    name="numero_de_empleados_con_calculo"
                    disabled
                  />
                </Col>
              </Row>
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    label="Fecha de Cálculo"
                    name="fecha_de_calculo"
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <CurrencyInput
                    label="Total ISR a Favor"
                    name="total_isr_favor"
                    disabled
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    label="No. de Empleados sin Cálculo Anual de ISR"
                    name="numero_de_empleados_sin_calculo"
                    disabled
                  />
                </Col>
                { selectedRowKeys.length
                  ? (
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estatus"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          dataSource={estadosFiltered}
                          disabled
                          onChange={(val) => {
                            if (val === ESTATUS_INACTIVO) {
                              setConfirmationVisibleEstatus(true);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )
                  : (
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estatus"
                        rules={rules.required}
                        hasFeedback
                        initialValue={1}
                      >
                        <Select
                          dataSource={estadosFiltered}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  )}
              </Row>
              {selectedRowKeys?.length ? (
                <IsrCalculoAnualDetalle
                  permission={permission}
                  selectedRowKeyEncabezado={selectedRowKeys[0]}
                  onClickEditEncabezado={onClickEdit}
                  fetchDataEncabezado={fetchData}
                  estatusInactivoAutorizadoCancelado={estatusAutorizado || estatusCancelado}
                />
              ) : null}
            </Card>
          )}
        </Form>
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleAutorizar(false);
          }}
          open={confirmationVisibleAutorizar}
          title="Confirmación"
          contentTitle={`Autorizar el ${handleContentPrint()}`}
          ConfirmAutorize
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationCancelar}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleCancelar(false);
          }}
          open={confirmationVisibleCancelar}
          title="Confirmación"
          contentTitle={`Cancelar el ${handleContentPrint()}`}
          ConfirmCancel
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          onFinishText="Continuar"
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          title="Confirmación"
          contentTitle={`Inactivar el ${handleContentPrint()}`}
          ConfirmInactive
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default IsrCalculoAnual;
